import React, { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import {
  LayerAnalyticsGrid,
  LayerAnalyticsGridContent,
} from './LayerAnalyticsGridContent';
import { LayerAnalyticsPieChart } from './LayerAnalyticsPieChart';
import {
  formatFigure,
  NO_DATA_MESSAGE,
  NO_FEATURES_MESSAGE,
  type LayerAnalyticsCard as LayerAnalyticsCardData,
  type LayerAnalyticsTable,
  type LayerAnalyticsTableRow,
} from './layerAnalytics.utils';
import { buildZoomColumn, type ZoomToTarget } from './layerAnalyticsZoomColumn';

type LayerAnalyticsCardTableProps = {
  table: LayerAnalyticsTable;
  rowIdPrefix: string;
  onZoomToTarget?: ZoomToTarget;
  canZoomToTarget: boolean;
};

const LayerAnalyticsCardTable: React.FC<LayerAnalyticsCardTableProps> = ({
  table,
  rowIdPrefix,
  onZoomToTarget,
  canZoomToTarget,
}) => {
  const columns = useMemo<ColumnDef<LayerAnalyticsTableRow>[]>(
    () => [
      ...(onZoomToTarget
        ? [
            buildZoomColumn<LayerAnalyticsTableRow>({
              subject: 'feature',
              onZoomToTarget,
              canZoomToTarget,
            }),
          ]
        : []),
      { id: 'name', header: table.nameLabel, accessorFn: (row) => row.name },
      ...table.columns.map(
        ({ key, label }): ColumnDef<LayerAnalyticsTableRow> => ({
          id: key,
          header: label,
          accessorFn: (row) => row.cells[key] ?? '',
        })
      ),
    ],
    [canZoomToTarget, onZoomToTarget, table.columns, table.nameLabel]
  );

  return (
    <>
      {table.title && <span className="fw-bold mb-0">{table.title}</span>}
      {table.rows.length === 0 ? (
        <em className="text-muted">{NO_FEATURES_MESSAGE}</em>
      ) : (
        <PratusTable
          columns={columns}
          data={table.rows}
          getRowId={(_row, index) => `${rowIdPrefix}-${index}`}
        />
      )}
    </>
  );
};

type LayerAnalyticsCardProps = {
  card: LayerAnalyticsCardData;
  /** Prefix for table row ids; unique per layer result. */
  rowIdPrefix: string;
  /** Enables the map-icon column (editor with a map only). */
  onZoomToTarget?: ZoomToTarget;
  canZoomToTarget?: boolean;
};

/**
 * The shared template every layer result is rendered with: an optional title, a
 * grid of figures, a row of donut charts and a feature table. Each part is left
 * out when the card has none of it; an entirely empty card says so.
 */
export const LayerAnalyticsCard: React.FC<LayerAnalyticsCardProps> = ({
  card,
  rowIdPrefix,
  onZoomToTarget,
  canZoomToTarget = false,
}) => {
  const { title, figures, charts, table } = card;
  const isEmpty = figures.length === 0 && charts.length === 0 && !table;

  return (
    <div className="d-flex flex-column gap-2 rounded border p-3">
      {title && <h5 className="fs-6 fw-bold mb-0">{title}</h5>}
      {figures.length > 0 && (
        <LayerAnalyticsGrid>
          {figures.map((figure) => (
            <LayerAnalyticsGridContent
              key={figure.label}
              label={figure.label}
              value={formatFigure(figure)}
            />
          ))}
        </LayerAnalyticsGrid>
      )}
      {charts.length > 0 && (
        <div className="d-flex gap-2">
          {charts.map((chart) => (
            <div key={chart.title} className="flex-fill w-50">
              <LayerAnalyticsPieChart
                title={chart.title}
                slices={chart.slices}
              />
            </div>
          ))}
        </div>
      )}
      {table && (
        <LayerAnalyticsCardTable
          table={table}
          rowIdPrefix={`${rowIdPrefix}-${card.key}`}
          onZoomToTarget={onZoomToTarget}
          canZoomToTarget={canZoomToTarget}
        />
      )}
      {isEmpty && <em className="text-muted">{NO_DATA_MESSAGE}</em>}
    </div>
  );
};
